import React, { useState, useMemo } from 'react';
import { Icon } from '../../Icon';
import { maskNumber, parseBrazilianNumber } from '../../../utils/formatUtils';

const FinancingCalculator = ({ type, onResultChange }: { type: 'price' | 'sac', onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [totalAmount, setTotalAmount] = useState<string>('100.000,00');
  const [interestRate, setInterestRate] = useState<string>('1,50');
  const [months, setMonths] = useState<string>('48');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const principal = parseFloat(parseBrazilianNumber(totalAmount)) || 0;
    const rate = parseFloat(parseBrazilianNumber(interestRate)) / 100; // taxa mensal
    const n = parseInt(months) || 0;

    if (principal <= 0 || rate <= 0 || n <= 0) return;

    let installments: Array<{ number: number; value: number; principal: number; interest: number; balance: number }> = [];
    let res: any;

    if (type === 'price') {
      // Tabela Price: parcelas fixas
      const installmentValue = principal * (rate * Math.pow(1 + rate, n)) / (Math.pow(1 + rate, n) - 1);
      let balance = principal;

      for (let i = 1; i <= n; i++) {
        const interestPayment = balance * rate;
        const principalPayment = installmentValue - interestPayment;
        balance -= principalPayment;

        installments.push({
          number: i,
          value: installmentValue,
          principal: principalPayment,
          interest: interestPayment,
          balance: Math.max(0, balance)
        });
      }

      res = {
        installmentValue,
        totalPaid: installmentValue * n,
        totalInterest: (installmentValue * n) - principal,
        installments,
        type: 'Price'
      };
    } else {
      // Tabela SAC: amortização constante
      const principalPayment = principal / n;
      let balance = principal;

      for (let i = 1; i <= n; i++) {
        const interestPayment = balance * rate;
        const installmentValue = principalPayment + interestPayment;
        balance -= principalPayment;

        installments.push({
          number: i,
          value: installmentValue,
          principal: principalPayment,
          interest: interestPayment,
          balance: Math.max(0, balance)
        });
      }

      const totalPaid = installments.reduce((sum, inst) => sum + inst.value, 0);
      const totalInterest = totalPaid - principal;

      res = {
        firstInstallment: installments[0]?.value || 0,
        lastInstallment: installments[installments.length - 1]?.value || 0,
        totalPaid,
        totalInterest,
        installments,
        type: 'SAC'
      };
    }

    setResult(res);

    const data = [
      { label: 'Valor Financiado', value: `R$ ${principal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Taxa de Juros', value: `${interestRate}% a.m.` },
      { label: 'Prazo', value: `${months} meses` },
      { label: 'Total Pago', value: `R$ ${res.totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Total Juros', value: `R$ ${res.totalInterest.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Tabela', value: res.type }
    ];

    if (type === 'price') {
      data.push({ label: 'Valor Parcela', value: `R$ ${res.installmentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` });
    } else {
      data.push({ label: '1ª Parcela', value: `R$ ${res.firstInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` });
      data.push({ label: 'Última Parcela', value: `R$ ${res.lastInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` });
    }

    onResultChange(data);
  };

  const hasResult = totalAmount && interestRate && months && result;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-indigo-50 to-slate-50 rounded-full border border-indigo-200 mb-4">
          <span className="text-2xl">🏦</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">
            Financiamento {type === 'price' ? 'Price' : 'SAC'}
          </span>
        </div>
        <p className="text-theme-muted text-sm">
          {type === 'price'
            ? 'Parcelas fixas do início ao fim (Tabela Price)'
            : 'Parcelas decrescentes com amortização constante (Tabela SAC)'}
        </p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Dados do Financiamento</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Valor Financiado (R$)</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={totalAmount}
                onChange={e => setTotalAmount(maskNumber(e.target.value))}
                placeholder="Ex: 100.000,00"
                className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400 focus:bg-theme-result pl-12"
              />
            </div>
            <div className="text-xs text-theme-muted mt-2">Valor total do bem</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Taxa de Juros (% a.m.)</label>
            <input
              type="text"
              value={interestRate}
              onChange={e => setInterestRate(maskNumber(e.target.value))}
              placeholder="Ex: 1,50"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Taxa mensal de juros</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Prazo (meses)</label>
            <input
              type="text"
              value={months}
              onChange={e => setMonths(e.target.value.replace(/\D/g, ''))}
              placeholder="Ex: 48"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Quantidade de parcelas</div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={calculate}
            className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
          >
            <Icon name="play" className="w-5 h-5" />
            Simular Financiamento
          </button>
        </div>
      </div>

      {hasResult && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] shadow-2xl border-2 bg-gradient-to-br from-indigo-500 to-purple-500 border-indigo-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">🏦</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {type === 'price' ? (
                <>
                  <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                    <p className="text-xs font-black uppercase opacity-70 mb-1">Valor da Parcela</p>
                    <p className="text-3xl font-black">R$ {result.installmentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs opacity-70 mt-1">fixa durante todo o prazo</p>
                  </div>
                  <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                    <p className="text-xs font-black uppercase opacity-70 mb-1">Total de Juros</p>
                    <p className="text-3xl font-black">R$ {result.totalInterest.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs opacity-70 mt-1">custo total do financiamento</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                    <p className="text-xs font-black uppercase opacity-70 mb-1">1ª Parcela</p>
                    <p className="text-3xl font-black">R$ {result.firstInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs opacity-70 mt-1">maior valor</p>
                  </div>
                  <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                    <p className="text-xs font-black uppercase opacity-70 mb-1">Última Parcela</p>
                    <p className="text-3xl font-black">R$ {result.lastInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs opacity-70 mt-1">menor valor</p>
                  </div>
                </>
              )}
            </div>

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
              <p className="text-xs font-black uppercase opacity-70 mb-1">Total Pago</p>
              <p className="text-3xl font-black">R$ {result.totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs opacity-70 mt-1">valor + juros</p>
            </div>

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">📋 Resumo do Financiamento</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Tipo:</span>
                  <span className="font-bold">Tabela {result.type}</span>
                </div>
                <div className="flex justify-between">
                  <span>Valor Financiado:</span>
                  <span className="font-bold">R$ {parseFloat(parseBrazilianNumber(totalAmount)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span>Taxa Mensal:</span>
                  <span className="font-bold">{interestRate}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Prazo:</span>
                  <span className="font-bold">{months} meses</span>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span>Juros Totais:</span>
                  <span className="font-bold">R$ {result.totalInterest.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!hasResult && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">🏦</div>
          <p className="font-bold text-base">Preencha os dados para simular o financiamento</p>
        </div>
      )}
    </div>
  );
};

export default FinancingCalculator;
